'use client'

import React from 'react'
import { Bar, Line, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, CartesianGrid, BarChart, LineChart } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface ChartContainerProps {
  children: React.ReactNode
  title: string
  className?: string
}

export function ChartContainer({ children, title, className }: ChartContainerProps) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            {React.Children.only(children) as React.ReactElement}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

interface BarChartProps {
  data: Array<Record<string, any>>
  xAxisDataKey: string
  barDataKey: string
  fill?: string
}

export function BarChartComponent({ data, xAxisDataKey, barDataKey, fill = "hsl(var(--chart-1))" }: BarChartProps) {
  return (
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey={xAxisDataKey} />
      <YAxis />
      <Tooltip />
      <Legend />
      <Bar dataKey={barDataKey} fill={fill} />
    </BarChart>
  )
}

interface LineChartProps {
  data: Array<Record<string, any>>
  xAxisDataKey: string
  lineDataKeys: string[]
}

export function LineChartComponent({ data, xAxisDataKey, lineDataKeys }: LineChartProps) {
  const colors = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))"
  ]

  return (
    <LineChart data={data}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey={xAxisDataKey} />
      <YAxis />
      <Tooltip />
      <Legend />
      {lineDataKeys.map((key, index) => (
        <Line key={key} type="monotone" dataKey={key} stroke={colors[index % colors.length]} />
      ))}
    </LineChart>
  )
}

interface PieChartProps {
  data: Array<Record<string, any>>
  dataKey: string
  nameKey: string
}

export function PieChartComponent({ data, dataKey, nameKey }: PieChartProps) {
  const colors = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))"
  ]

  return (
    <Pie
      data={data}
      dataKey={dataKey}
      nameKey={nameKey}
      cx="50%"
      cy="50%"
      outerRadius={80}
      fill="hsl(var(--chart-1))"
      label
    >
      {data.map((entry, index) => (
        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
      ))}
    </Pie>
  )
}

